import { notification } from "antd";
import { all, call, put, takeLatest } from "redux-saga/effects";
import {
  createProjectFailure,
  createProjectSuccess,
  getProjectFailure,
  getProjectSuccess,
  updateProjectFailure,
  updateProjectSuccess,
} from "./action.js";
import {
  CREATE_PROJECT_REQUEST,
  GET_CURRENT_PROJECT,
  GET_PROJECT_REQUEST,
  PROJECT_LOADER_START,
  PROJECT_LOADER_STOP,
  UPDATE_PROJECT_REQUEST,
} from "./types.js";
import { ADMIN_URL } from "../../config.js";
import fetch from "../../services/fetch.js";

// Create Project API call
function* createProjectSaga(action) {
  try {
    yield put({ type: PROJECT_LOADER_START });

    const res = yield fetch(`${ADMIN_URL}/v1/project/project-creation/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(action.payload),
    });

    if (res.status === 403) {
      throw new Error("Unauthorized access. Please check your permissions.");
    }

    const response = yield res.json();

    // Check for the new success response structure
    if (response.statusCode === 200 && response.data?.status === "Success") {
      yield put(createProjectSuccess(response.data));
      notification.success({
        message: "Project created successfully",
        duration: 3,
      });
      yield put({ type: GET_PROJECT_REQUEST });
      return;
    }

    // Handle other error cases
    const errorMessage = response.message || "Failed to create project";
    yield put(createProjectFailure(errorMessage));
    notification.error({
      message: errorMessage,
      duration: 6,
    });
  } catch (error) {
    console.error("Project creation error:", error);
    const errorMessage =
      error.message || "Failed to create project. Please try again.";
    yield put(createProjectFailure(errorMessage));
    notification.error({
      message: errorMessage,
      duration: 6,
    });
  } finally {
    yield put({ type: PROJECT_LOADER_STOP });
  }
}

// Get Project Details API call
function* getProjectSaga() {
  try {
    yield put({ type: PROJECT_LOADER_START });

    const res = yield fetch(`${ADMIN_URL}/v1/project/get-projects`, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    if (res.status === 403) {
      throw new Error("Unauthorized");
    }
    const response = yield res.json();

    // Check HTTP status first
    if (res.status === 200) {
      // If response has data, consider it a success regardless of response.status
      if (response.data) {
        yield put(getProjectSuccess(response.data));
        return;
      }
    }

    // Handle error cases silently for GET requests after project creation
    if (response.status === 410) {
      yield put(getProjectFailure(response.message));
      notification.warn({
        message: response.message || "Resource not found",
        duration: 6,
      });
    } else {
      yield put(getProjectFailure(response.message || "Something went wrong"));
      notification.error({
        message: response.message || "Something went wrong",
      });
    }
  } catch (error) {
    console.error("Error in getProjectSaga:", error);
    yield put(getProjectFailure(error.message));
    notification.error({
      message: error.message || "Something went wrong",
    });
  } finally {
    yield put({ type: PROJECT_LOADER_STOP });
  }
}

// Update Project API call
function* updateProjectSaga(action) {
  try {
    yield put({ type: PROJECT_LOADER_START });
    const { projectId, projectData, currentProjectId } = action.payload;
    const res = yield fetch(
      `${ADMIN_URL}/v1/project/edit-project/${projectId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(projectData),
      }
    );

    if (res.status === 403) {
      throw new Error("Unauthorized");
    }

    const response = yield res.json();

    if (res.status === 200) {
      yield put(updateProjectSuccess(response.data));
      yield put({ type: GET_PROJECT_REQUEST });
      yield put({
        type: GET_CURRENT_PROJECT,
        payload: { project_id: currentProjectId },
      });
      notification.success({
        message: "Project updated successfully",
        duration: 3,
      });
      return;
    }

    if (response.status === 410) {
      yield put(updateProjectFailure(response.message));
      notification.warn({
        message: response.message,
        duration: 6,
      });
    } else {
      yield put(updateProjectFailure("Something went wrong"));
      notification.error({
        message: "Something went wrong.",
      });
    }
  } catch (error) {
    yield put(updateProjectFailure(error.message));
    notification.error({
      message: error.message || "Something went wrong",
    });
  } finally {
    yield put({ type: PROJECT_LOADER_STOP });
  }
}

function* projectWatcher() {
  yield takeLatest(CREATE_PROJECT_REQUEST, createProjectSaga);
  yield takeLatest(GET_PROJECT_REQUEST, getProjectSaga);
  yield takeLatest(UPDATE_PROJECT_REQUEST, updateProjectSaga);
}

export default function* projectSagas() {
  yield all([call(projectWatcher)]);
}
